import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import useToast from '../../../util/hooks/useToast';
import { useAppDispatch } from '../../../redux/hooks';
import { setUserResetOption } from '../../../redux/slices/auth/forgotPasswordSlice';

import Input from '../../../components/Input';
import Button from '../../../components/Button';
import ArrowRight from '../../../assets/svg_components/ArrowRight';
import { setEmail, setPhone } from '../../../redux/slices/user/userSlice';
import ForgotPasswordVerifyScreen from '../ForgotPasswordVerifyScreen';
import AuthHeader from '../../../components/AuthBackground/_partials/AuthHeader';
import { ReactComponent as EmailInputIcon } from '../../../assets/images/email-input-icon.svg';
import { ReactComponent as RadioButtonIcon } from '../../../assets/images/round-check-icon.svg';
import { ReactComponent as RadioButtonFillIcon } from '../../../assets/images/round-checked-icon.svg';
import {
  emailValidation,
  phoneValidation
} from '../../../modal_views/auth/ForgotPasswordIdentityModal/ForgotPasswordIdentityModal.validation';
import { FORGOT_PASSWORD_BY_EMAIL, FORGOT_PASSWORD_BY_PHONE } from '../../../graphql/mutation';
import PhoneInputField from '../../../components/Input/PhoneInputField';

import ForgotPassStyles from './ForgotPasswordScreen.module.scss';

const tabs = [
  {
    title: 'Email address',
    key: 'EMAIL'
  },
  {
    title: 'Phone number',
    key: 'PHONE'
  }
];

const ForgotPassword = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('EMAIL');
  const [forgotPasswordByPhone] = useMutation(FORGOT_PASSWORD_BY_PHONE);
  const [forgotPasswordByEmail] = useMutation(FORGOT_PASSWORD_BY_EMAIL);
  const Toast = useToast();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [otpVerify, setOtpVerify] = useState(false);

  useEffect(() => {
    if (error) {
      Toast.error(error);
    }
  }, [error, setError]);

  const resetByPhoneFormik = useFormik({
    validationSchema: phoneValidation,
    initialValues: {
      phone: ''
    },
    onSubmit: async (values) => {
      try {
        const payload = {
          phone: `+${values?.phone}`
        };
        setIsLoading(true);
        setError('');
        const resp = await forgotPasswordByPhone({ variables: payload });
        if (resp?.data?.forgotPasswordByPhone?.status === 200) {
          dispatch(setPhone(`+${values.phone}`));
          dispatch(setUserResetOption(activeTab));
          setOtpVerify(true);
          setIsLoading(false);
        }
        if (resp?.data?.forgotPasswordByPhone?.status === 400) {
          setError(resp?.data?.forgotPasswordByPhone?.message);
          setIsLoading(false);
        }
      } catch (err) {
        setIsLoading(false);
        const e = err as any;
        if (e && e.graphQLErrors && e.graphQLErrors.length > 0) {
          setError(e.graphQLErrors[0].message);
        } else {
          setError('Something went wrong');
        }
      }
    }
  });

  const resetByEmailFormik = useFormik({
    validationSchema: emailValidation,
    initialValues: {
      email: ''
    },
    onSubmit: async (values) => {
      try {
        const payload = {
          email: values.email
        };
        setIsLoading(true);
        setError('');
        const resp = await forgotPasswordByEmail({ variables: payload });
        if (resp?.data?.forgotPassword?.status === 200) {
          dispatch(setEmail(values.email));
          dispatch(setUserResetOption(activeTab));
          setOtpVerify(true);
          setIsLoading(false);
        }
        if (resp?.data?.forgotPassword?.status === 400) {
          setError(resp?.data?.forgotPassword?.message);
          setIsLoading(false);
        }
      } catch (err) {
        setIsLoading(false);
        const e = err as any;
        if (e && e?.graphQLErrors && e?.graphQLErrors?.length > 0) {
          setError(e?.graphQLErrors[0]?.message);
        } else {
          setError('Something went wrong');
        }
      }
    }
  });

  return (
    <div className={ForgotPassStyles.forgotPass}>
      <AuthHeader className={ForgotPassStyles.forgotPass__authHeader} />
      {!otpVerify && (
        <div className={ForgotPassStyles.forgotPass__content}>
          <h1 className={ForgotPassStyles.forgotPass__headText}>Forgot Your Password?</h1>
          <p className={ForgotPassStyles.forgotPass__subtext}>
            Please enter your registered information to reset your password
          </p>
          <div className={ForgotPassStyles.forgotPass__tab}>
            <p className={ForgotPassStyles.forgotPass__tabInstruction}>
              Choose password retrieval option
            </p>
            <div className={ForgotPassStyles.forgotPass__tabGroup}>
              {tabs.map((tab) => (
                <span key={tab.key} className={ForgotPassStyles.forgotPass__tabOptions}>
                  {activeTab === tab.key ? (
                    <RadioButtonFillIcon
                      onClick={() =>
                        activeTab === 'EMAIL' ? setActiveTab('PHONE') : setActiveTab('EMAIL')
                      }
                      className={ForgotPassStyles.forgotPass__tabCheck}
                    />
                  ) : (
                    <RadioButtonIcon
                      onClick={() =>
                        activeTab === 'PHONE' ? setActiveTab('EMAIL') : setActiveTab('PHONE')
                      }
                      className={ForgotPassStyles.forgotPass__tabCheck}
                    />
                  )}
                  <span
                    className={`${ForgotPassStyles.forgotPass__tabLink} ${
                      activeTab === tab.key
                        ? ForgotPassStyles['forgotPass__tabLink--active']
                        : ForgotPassStyles['forgotPass__tabLink--inactive']
                    }`}
                    onClick={() => setActiveTab(tab.key)}>
                    {tab.title}
                  </span>
                </span>
              ))}
            </div>
          </div>

          <form
            onSubmit={
              activeTab === 'EMAIL'
                ? resetByEmailFormik.handleSubmit
                : resetByPhoneFormik.handleSubmit
            }
            className={ForgotPassStyles.forgotPass__form}>
            {activeTab === 'EMAIL' ? (
              <>
                <div className={ForgotPassStyles.forgotPass__input}>
                  <Input
                    type="email"
                    name="email"
                    icon={<EmailInputIcon />}
                    label="Email"
                    placeholder="e.g. user@email.com"
                    value={resetByEmailFormik.values.email}
                    onChange={resetByEmailFormik.handleChange}
                    error={resetByEmailFormik.submitCount > 0 && resetByEmailFormik.errors.email}
                  />
                </div>

                <Button
                  className={ForgotPassStyles.forgotPass__button}
                  title="Verify email address"
                  color="primary"
                  type="submit"
                  isLoading={isLoading}
                  disabled={resetByEmailFormik.isSubmitting || isLoading}
                />
              </>
            ) : (
              <>
                <div className={ForgotPassStyles.forgotPass__input}>
                  <PhoneInputField
                    onChange={(value) => {
                      resetByPhoneFormik.setFieldValue('phone', value);
                    }}
                    value={resetByPhoneFormik.values.phone}
                    disabled={false}
                    error={resetByPhoneFormik.submitCount > 0 && resetByPhoneFormik.errors.phone}
                    name="phone"
                    label=""
                    placeholder="Phone number"
                    isShowLabel={false}
                  />
                </div>

                <Button
                  className={ForgotPassStyles.forgotPass__button}
                  title="Verify phone number"
                  color="primary"
                  type="submit"
                  isLoading={isLoading}
                  disabled={resetByPhoneFormik.isSubmitting || isLoading}
                />
              </>
            )}
          </form>
          <div className={ForgotPassStyles.forgotPass__redirect}>
            <div className={ForgotPassStyles.forgotPass__redirectText}>
              <ArrowRight className={ForgotPassStyles.forgotPass__redirectArrow} stroke="#5941A9" />
              <span
                className={ForgotPassStyles.forgotPass__redirectClickable}
                onClick={() => {
                  navigate('/');
                }}>
                Go back to login
              </span>
            </div>
          </div>
        </div>
      )}
      {otpVerify && <ForgotPasswordVerifyScreen />}
    </div>
  );
};

export default ForgotPassword;
